import type {
  AdminTheory,
  Analytics,
  MediaItem,
  Neighbors,
  PodcastReady,
  PodcastSettings,
  SessionInfo,
  SiteSettings,
  Stats,
  TagCount,
  Theory,
  TheorySummary,
} from './types'

export class ApiError extends Error {
  status: number
  code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options
  const isFormData = body instanceof FormData

  const finalHeaders = new Headers(headers)
  if (body !== undefined && !isFormData) finalHeaders.set('Content-Type', 'application/json')

  // The server pairs this header against an httpOnly-adjacent cookie; a
  // cross-origin page can read neither, which is what stops CSRF.
  const csrf = readCookie('sms_csrf')
  if (csrf) finalHeaders.set('X-CSRF-Token', csrf)

  let response: Response
  try {
    response = await fetch(path, {
      ...rest,
      headers: finalHeaders,
      credentials: 'same-origin',
      body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
    })
  } catch {
    throw new ApiError('Could not reach the server. Is it running?', 0, 'network')
  }

  if (response.status === 204) return undefined as T

  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('application/json')) {
    if (!response.ok) throw new ApiError(`Request failed (${response.status}).`, response.status)
    return undefined as T
  }

  const payload = await response.json()
  if (!response.ok) {
    throw new ApiError(payload?.error ?? `Request failed (${response.status}).`, response.status, payload?.code)
  }
  return payload as T
}

const query = (params: Record<string, string | number | boolean | undefined>) => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '' && value !== false) search.set(key, String(value))
  }
  const string = search.toString()
  return string ? `?${string}` : ''
}

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

export const api = {
  theories: (
    params: { search?: string; tag?: string; sort?: string; limit?: number } = {},
  ) => request<{ theories: TheorySummary[]; total: number }>(`/api/theories${query(params)}`),

  theory: (slug: string) =>
    request<{ theory: Theory; neighbors: Neighbors }>(`/api/theories/${encodeURIComponent(slug)}`),

  tags: () => request<{ tags: TagCount[] }>('/api/tags'),

  /** `not` is the slug you're already on, so the dice never lands where you are. */
  random: (not?: string) => request<{ slug: string }>(`/api/random${query({ not })}`),

  /** Published-only counts — the admin's /stats is the one that includes drafts. */
  site: () =>
    request<{
      stats: Pick<
        Stats,
        'published' | 'withAudio' | 'views' | 'listens' | 'audioSeconds' | 'avgCredibility'
      >
      /** Which optional furniture the admin has switched on. */
      display: { stats: boolean; views: boolean; filters: boolean }
    }>('/api/site'),

  /** Fire-and-forget: a failed listen count must never interrupt playback. */
  countListen: (slug: string) =>
    request(`/api/theories/${encodeURIComponent(slug)}/listen`, { method: 'POST' }).catch(() => {}),
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export const adminApi = {
  session: () =>
    request<{ authenticated: boolean; passwordConfigured: boolean; csrfToken: string | null }>(
      '/api/admin/session',
    ),

  login: (password: string) =>
    request<{ ok: true; csrfToken: string }>('/api/admin/login', {
      method: 'POST',
      body: { password },
    }),

  logout: () => request<{ ok: true }>('/api/admin/logout', { method: 'POST' }),

  list: (params: { status?: string; search?: string; sort?: string } = {}) =>
    request<{ theories: AdminTheory[]; total: number }>(`/api/admin/theories${query(params)}`),

  get: (id: number) => request<{ theory: AdminTheory }>(`/api/admin/theories/${id}`),

  create: (data: Partial<AdminTheory>) =>
    request<{ theory: AdminTheory }>('/api/admin/theories', { method: 'POST', body: data }),

  update: (id: number, data: Partial<AdminTheory>) =>
    request<{ theory: AdminTheory }>(`/api/admin/theories/${id}`, { method: 'PUT', body: data }),

  remove: (id: number) =>
    request<{ ok: true }>(`/api/admin/theories/${id}`, { method: 'DELETE' }),

  previewSlug: (value: string, id?: number) =>
    request<{ slug: string }>('/api/admin/slug', { method: 'POST', body: { value, id } }),

  stats: () => request<{ stats: Stats; tags: TagCount[] }>('/api/admin/stats'),

  /** XHR rather than fetch, because upload progress needs a real progress event. */
  upload: (
    file: File,
    options: { seconds?: number | null; onProgress?: (percent: number) => void } = {},
  ) =>
    new Promise<MediaItem>((resolve, reject) => {
      const form = new FormData()
      form.append('file', file)
      if (options.seconds) form.append('seconds', String(options.seconds))

      const xhr = new XMLHttpRequest()
      xhr.open('POST', '/api/admin/media')
      xhr.withCredentials = true

      const csrf = readCookie('sms_csrf')
      if (csrf) xhr.setRequestHeader('X-CSRF-Token', csrf)

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          options.onProgress?.(Math.round((event.loaded / event.total) * 100))
        }
      })

      xhr.addEventListener('load', () => {
        let payload: { media?: MediaItem; error?: string } = {}
        try {
          payload = JSON.parse(xhr.responseText)
        } catch {
          /* fall through to the generic message below */
        }
        if (xhr.status >= 200 && xhr.status < 300 && payload.media) resolve(payload.media)
        else reject(new ApiError(payload.error ?? `Upload failed (${xhr.status}).`, xhr.status))
      })

      xhr.addEventListener('error', () => reject(new ApiError('Upload failed — connection lost.', 0)))
      xhr.addEventListener('abort', () => reject(new ApiError('Upload cancelled.', 0, 'aborted')))

      xhr.send(form)
    }),

  analytics: (days: number) => request<Analytics>(`/api/admin/analytics?days=${days}`),

  settings: () =>
    request<{
      sessions: SessionInfo[]
      site: SiteSettings
      podcast: PodcastSettings
      podcastReady: PodcastReady
      categories: string[]
    }>('/api/admin/settings'),

  saveSite: (site: Partial<SiteSettings>) =>
    request<{ site: SiteSettings }>('/api/admin/settings/site', { method: 'PUT', body: site }),

  savePodcast: (podcast: Partial<PodcastSettings>) =>
    request<{ podcast: PodcastSettings; podcastReady: PodcastReady }>(
      '/api/admin/settings/podcast',
      { method: 'PUT', body: podcast },
    ),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true; message: string }>('/api/admin/password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    }),

  revokeSessions: () => request<{ ok: true }>('/api/admin/sessions/revoke-all', { method: 'POST' }),
}
